/**
 * File kinds: the user-facing categories of the workspace Type filter, file
 * icons and the upload allowlist check.
 *
 * A kind is COMPUTED, never stored. Artifacts store raw facts only
 * (`artifactType`, `mimeType`, `fileExtension`), and this registry is the one
 * place that turns those facts into a category. It is also the input to the
 * backend Prisma `where` builder, so every matcher is a plain list:
 * `artifactTypes` (artifactType IN), `mimeExact` (mimeType equals),
 * `mimePrefixes` (mimeType startsWith) and `extensions` (fileExtension IN;
 * lowercase, no leading dot).
 *
 * Two rules below are finer than a flat list can express: `.raw` (mass spec
 * unless the MIME is `image/*`) and the ambiguous webm/ogg containers (audio
 * unless the MIME is `video/*`). `classifyFile` hard-codes them, and the
 * backend where builder (`services/workspace/items/kind-filter.ts`)
 * reproduces them exactly; its test fails if a new one is added here only.
 */
import { AMBIGUOUS_MEDIA_CONTAINER_EXTENSIONS, AUDIO_EXTENSIONS, CODE_EXTENSIONS, DOCUMENT_EXTENSIONS, IMAGE_EXTENSIONS, VIDEO_EXTENSIONS, } from '../utils/fileTypeDetection';
const INSTRUMENT_TECHNIQUES = [
    {
        id: 'microscopy',
        label: 'Microscopy',
        extensions: ['czi', 'nd2', 'lif', 'oib', 'oif', 'ims', 'ome.tiff', 'ome.tif'],
    },
    { id: 'flow', label: 'Flow cytometry', extensions: ['fcs'] },
    {
        id: 'mass-spec',
        label: 'Mass spectrometry',
        // `.raw` is mass spec only when the MIME is not `image/*`; under an image
        // MIME it is a camera RAW photo (see classifyFile). Bruker `.d` is a
        // directory, so it arrives zipped and classifies as an archive.
        extensions: ['raw', 'wiff', 'mzml', 'mzxml'],
    },
    {
        id: 'sequencing',
        label: 'Sequencing',
        extensions: ['fastq', 'fq', 'fasta', 'bam', 'sam', 'vcf', 'ab1'],
    },
    {
        id: 'chromatography-spectroscopy',
        label: 'Chromatography and spectroscopy',
        extensions: ['cdf', 'jdx', 'dx', 'spc'],
    },
    { id: 'qpcr-plate', label: 'qPCR and plate readers', extensions: ['eds', 'rdml'] },
    { id: 'electrophysiology', label: 'Electrophysiology', extensions: ['abf', 'nwb'] },
    { id: 'structures', label: 'Structures', extensions: ['pdb', 'cif', 'mrc'] },
];
export const FILE_KINDS = [
    // Folders are projects, not artifacts: classifyFile never returns this kind.
    // label is today's user-facing word, not the id: the Collections -> Folders
    // rename is deferred (David, 2026-09-29). Must match entityIcons'
    // pluralName for 'project' (packages/frontend/src/theme/entityIcons.ts)
    // until that rename ships.
    { id: 'folders', label: 'Collections', glyph: 'folder' },
    { id: 'notes', label: 'Notes', glyph: 'note', artifactTypes: ['note'] },
    { id: 'protocols', label: 'Protocols', glyph: 'protocol', artifactTypes: ['protocol'] },
    {
        // Uploaded workbooks count too: a researcher filtering for spreadsheets
        // means "my data tables", whether or not intake converted the file into a
        // native spreadsheet yet.
        id: 'spreadsheets',
        label: 'Spreadsheets',
        glyph: 'spreadsheet',
        artifactTypes: ['spreadsheet'],
        mimeExact: [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel.sheet.macroenabled.12',
            'application/vnd.oasis.opendocument.spreadsheet',
            'text/csv',
            'text/tab-separated-values',
        ],
        extensions: ['xlsx', 'xls', 'xlsm', 'ods', 'numbers', 'csv', 'tsv'],
    },
    {
        id: 'documents',
        label: 'Documents',
        glyph: 'document',
        mimeExact: [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/rtf',
            'text/rtf',
            'application/vnd.oasis.opendocument.text',
            'text/plain',
            'text/markdown',
            'text/x-markdown',
        ],
        extensions: [...DOCUMENT_EXTENSIONS, 'txt', 'rtf', 'odt', 'md', 'pages'],
    },
    {
        id: 'presentations',
        label: 'Presentations',
        glyph: 'presentation',
        mimeExact: [
            'application/vnd.ms-powerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'application/vnd.oasis.opendocument.presentation',
        ],
        extensions: ['ppt', 'pptx', 'odp', 'key'],
    },
    {
        id: 'images',
        label: 'Images',
        glyph: 'image',
        mimePrefixes: ['image/'],
        // heic/heif are added here, NOT in the shared IMAGE_EXTENSIONS list: they
        // classify an octet-stream upload as an image for the Type filter and
        // upload allowlist, but Chrome and Firefox cannot decode HEIC/HEIF in an
        // <img> (only Safari 17+ can), so isImageFile (the render boundary) must
        // stay false for them.
        extensions: [...IMAGE_EXTENSIONS, 'heic', 'heif'],
    },
    {
        id: 'video',
        label: 'Video',
        glyph: 'video',
        mimePrefixes: ['video/'],
        // mkv is added here, NOT in the shared VIDEO_EXTENSIONS list: it
        // classifies an octet-stream upload as video for the Type filter and
        // upload allowlist, but not every browser can play an MKV in a <video>,
        // so isVideoFile (the render boundary) must stay false for it.
        extensions: [...VIDEO_EXTENSIONS, 'mkv'],
    },
    {
        // Includes the ambiguous webm/ogg containers; a video/* MIME moves them
        // to video (see classifyFile).
        id: 'audio',
        label: 'Audio',
        glyph: 'audio',
        mimePrefixes: ['audio/'],
        extensions: AUDIO_EXTENSIONS,
    },
    {
        id: 'instrument',
        label: 'Instrument data',
        glyph: 'instrument',
        techniques: INSTRUMENT_TECHNIQUES,
    },
    {
        id: 'code',
        label: 'Code',
        glyph: 'code',
        mimeExact: [
            'application/json',
            'application/xml',
            'text/xml',
            'text/x-r-source',
            'text/x-python',
            'text/x-ruby',
            'application/sql',
            'application/x-sh',
            'text/x-julia',
            'text/x-matlab',
            'text/x-stata',
            'text/x-c',
            'text/x-java-source',
            'text/css',
            'application/x-yaml',
            'text/javascript',
            'application/javascript',
        ],
        extensions: CODE_EXTENSIONS,
    },
    {
        id: 'archives',
        label: 'Archives',
        glyph: 'archive',
        mimeExact: [
            'application/zip',
            'application/x-zip-compressed',
            'application/x-rar-compressed',
            'application/vnd.rar',
            'application/x-7z-compressed',
            'application/x-tar',
            'application/gzip',
            'application/x-gzip',
            'application/x-bzip2',
            'application/x-xz',
        ],
        extensions: ['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'bz2', 'xz'],
    },
    { id: 'equipment', label: 'Equipment', glyph: 'equipment', artifactTypes: ['equipment'] },
    // A shortcut to an outside page (docs/planning/link-artifacts.md). Its own
    // kind, so the Type filter can find links.
    { id: 'links', label: 'Links', glyph: 'link', artifactTypes: ['link'] },
    { id: 'other', label: 'Other', glyph: 'file' },
];
/**
 * Compound suffixes that name a different format than their last segment:
 * every registered extension (kind or technique) containing a dot.
 * `fileExtensionFromName` keeps these whole so `stack.ome.tiff` is stored as
 * `ome.tiff` (microscopy) rather than `tiff` (image). Derived from
 * `FILE_KINDS` rather than hand-repeated, so a new dotted extension added to a
 * technique or kind is picked up automatically. Longest-first: a nested
 * compound (`a.b.c` alongside `b.c`) must be matched before its own suffix.
 */
export const COMPOUND_FILE_EXTENSIONS = Array.from(new Set(FILE_KINDS.flatMap((kind) => [
    ...(kind.extensions ?? []),
    ...(kind.techniques ?? []).flatMap((t) => t.extensions),
])).values())
    .filter((ext) => ext.includes('.'))
    .sort((a, b) => b.length - a.length);
/**
 * Every valid Type filter id, in registry order: the canonical order of the
 * `kinds` URL param. The Type menu DISPLAYS kinds in its own sections
 * (TypeFilterMenu's KIND_SECTIONS); this order is not that one.
 */
export const FILE_KIND_FILTER_IDS = [
    ...FILE_KINDS.map((kind) => kind.id),
    ...INSTRUMENT_TECHNIQUES.map((t) => `instrument.${t.id}`),
];
/**
 * The Type sort's file groups, in the A to Z order of the singular label the
 * list's Kind column shows for each (`fileKindLabel` in the frontend's
 * `itemLabels.ts`; its test fails if a label change breaks this order).
 * Instrument data sorts by its technique ("Microscopy"), which is what the
 * column shows; every instrument file has one, so there is no bare
 * `instrument` group. The backend pages the files phase of `sort=kind` one
 * group at a time in this order (`services/workspace/items/page.ts`).
 */
export const FILE_KIND_SORT_GROUPS = [
    'archives', // Archive
    'other', // Attachment
    'audio', // Audio
    'instrument.chromatography-spectroscopy', // Chromatography and spectroscopy
    'code', // Code
    'documents', // Document
    'instrument.electrophysiology', // Electrophysiology
    'equipment', // Equipment
    'instrument.flow', // Flow cytometry
    'images', // Image
    'links', // Link
    'instrument.mass-spec', // Mass spectrometry
    'instrument.microscopy', // Microscopy
    'notes', // Note
    'presentations', // Presentation
    'protocols', // Protocol
    'instrument.qpcr-plate', // qPCR and plate readers
    'instrument.sequencing', // Sequencing
    'spreadsheets', // Spreadsheet
    'instrument.structures', // Structures
    'video', // Video
];
/**
 * The stored `fileExtension` for a filename: lowercase, no dot, compound
 * suffixes kept whole. Null when the name has no extension.
 */
export const fileExtensionFromName = (fileName) => {
    const lower = fileName.toLowerCase();
    const compound = COMPOUND_FILE_EXTENSIONS.find((ext) => lower.endsWith(`.${ext}`));
    if (compound)
        return compound;
    const dot = lower.lastIndexOf('.');
    return dot === -1 || dot === lower.length - 1 ? null : lower.slice(dot + 1);
};
/**
 * The stored `mimeType`: lowercase, parameters stripped (`image/HEIC; charset=x`
 * is `image/heic`). Null when the value is not a string or is empty.
 */
export const normalizeMimeType = (mimeType) => typeof mimeType === 'string' ? mimeType.split(';')[0].trim().toLowerCase() || null : null;
/**
 * The `mimeType` and `fileExtension` columns an artifact's `metadata` implies,
 * from its `fileName` and `mimeType` keys (null when absent or not a string).
 * The one derivation: the Prisma write extension, the client metadata merge
 * and the backfill all call this, so a column never disagrees with its row.
 */
export const fileFactsFromMetadata = (metadata) => {
    const record = metadata !== null && typeof metadata === 'object' && !Array.isArray(metadata)
        ? metadata
        : {};
    return {
        mimeType: normalizeMimeType(record.mimeType),
        fileExtension: typeof record.fileName === 'string' ? fileExtensionFromName(record.fileName) : null,
    };
};
const EXTENSION_INDEX = new Map();
for (const kind of FILE_KINDS) {
    for (const ext of kind.extensions ?? [])
        EXTENSION_INDEX.set(ext, { kind: kind.id });
    for (const t of kind.techniques ?? []) {
        for (const ext of t.extensions)
            EXTENSION_INDEX.set(ext, { kind: kind.id, technique: t.id });
    }
}
/** Classify one file from raw facts. Order matters: Bower-native type, then extension, then MIME. */
export function classifyFile(facts) {
    const native = FILE_KINDS.find((kind) => kind.artifactTypes?.some((type) => type === facts.artifactType));
    if (native)
        return { kind: native.id };
    const mime = normalizeMimeType(facts.mimeType);
    const ext = facts.fileExtension?.trim().toLowerCase().replace(/^\./, '') || null;
    if (ext) {
        if (ext === 'raw' && mime?.startsWith('image/'))
            return { kind: 'images' };
        if (AMBIGUOUS_MEDIA_CONTAINER_EXTENSIONS.includes(ext) && mime?.startsWith('video/')) {
            return { kind: 'video' };
        }
        const byExtension = EXTENSION_INDEX.get(ext);
        if (byExtension)
            return { ...byExtension };
    }
    if (mime) {
        const exact = FILE_KINDS.find((kind) => kind.mimeExact?.includes(mime));
        if (exact)
            return { kind: exact.id };
        const prefixed = FILE_KINDS.find((kind) => kind.mimePrefixes?.some((p) => mime.startsWith(p)));
        if (prefixed)
            return { kind: prefixed.id };
    }
    return { kind: 'other' };
}
//# sourceMappingURL=fileKinds.js.map